import React, { useState, useMemo } from 'react';
import { X, Search, MapPin, AlarmClock, Zap } from 'lucide-react';

/**
 * Modal that displays a searchable list of deliveries for a given status/category.
 *
 * @param {boolean}  isOpen              - Controls visibility
 * @param {function} onClose             - Close handler
 * @param {string}   title               - Modal title (e.g. "Entregados")
 * @param {Array}    limaItems           - Lima deliveries to show
 * @param {Array}    provinciaItems      - Provincia deliveries to show
 * @param {Array}    tabs                - Optional custom tabs: [{ label, items }]
 * @param {boolean}  showPostponedReason - When true, renders postponed_reason under each row
 * @param {Array}    expressItems        - Express deliveries to show
 */
export function DeliveryListModal({
  isOpen,
  onClose,
  title,
  limaItems = [],
  provinciaItems = [],
  tabs = null,
  showPostponedReason = false,
  expressItems = [],
}) {
  const [search, setSearch] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const standardTabs = useMemo(() => {
    const result = [];
    if (limaItems.length > 0)      result.push({ label: 'Lima',     items: limaItems,     type: 'standard' });
    if (provinciaItems.length > 0) result.push({ label: 'Provincia', items: provinciaItems, type: 'standard' });
    if (expressItems.length > 0)   result.push({ label: 'Express',  items: expressItems,  type: 'express'  });
    return result;
  }, [limaItems, provinciaItems, expressItems]);

  // If caller passed expressItems directly without tabs, use standardTabs
  // If caller passed a pure expressItems modal (no lima/provincia), still works
  const activeTabs = tabs !== null ? tabs : standardTabs;

  const activeTab = activeTabs[activeTabIndex];
  const isExpressTab = activeTab?.type === 'express';

  React.useEffect(() => {
    if (isOpen) {
      setActiveTabIndex(0);
      setSearch('');
    }
  }, [isOpen]);

  const currentItems = activeTab?.items ?? [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return currentItems;
    return currentItems.filter(d => {
      const baseMatch =
        String(d.id).includes(q) ||
        (d.recipient_name ?? d.name ?? '').toLowerCase().includes(q) ||
        (d.recipient_phone ?? d.phone ?? '').toLowerCase().includes(q);

      if (isExpressTab) {
        return baseMatch ||
          (d.pickup_address  ?? '').toLowerCase().includes(q) ||
          (d.dropoff_address ?? '').toLowerCase().includes(q);
      }

      return baseMatch ||
        (d.district ?? d.recipient_district ?? '').toLowerCase().includes(q) ||
        (showPostponedReason && (d.postponed_reason ?? '').toLowerCase().includes(q));
    });
  }, [currentItems, search, showPostponedReason, isExpressTab]);

  if (!isOpen) return null;

  function getStatusColor(status) {
    switch (status) {
      case 'pending':   return 'bg-orange-100 text-orange-700';
      case 'encamino':  return 'bg-blue-100 text-blue-700';
      case 'entregado': return 'bg-green-100 text-green-700';
      case 'cancelado':
      case 'rechazado': return 'bg-red-100 text-red-700';
      default:          return 'bg-slate-100 text-slate-600';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending:    'Pendiente',
      encamino:   'En Camino',
      entregado:  'Entregado',
      cancelado:  'Cancelado',
      rechazado:  'Rechazado',
      postergado: 'Postergado',
    };
    return labels[status] || status;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-extrabold text-slate-900 font-montserrat italic flex items-center gap-2">
            {isExpressTab && <Zap className="w-4 h-4 text-brand-green" />}
            {title}
            <span className="ml-1 text-sm font-semibold text-slate-400 not-italic">
              ({currentItems.length})
            </span>
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Tabs — only render if more than one */}
        {activeTabs.length > 1 && (
          <div className="flex border-b border-slate-200 px-5">
            {activeTabs.map((tab, i) => {
              const isExpress = tab.type === 'express';
              const activeColor = isExpress ? 'border-brand-green text-brand-green' : 'border-[#B1336E] text-[#B1336E]';
              const activeBadge = isExpress ? 'bg-brand-green/10 text-brand-green' : 'bg-[#B1336E]/10 text-[#B1336E]';
              return (
                <button
                  key={tab.label}
                  onClick={() => { setActiveTabIndex(i); setSearch(''); }}
                  className={`py-2 px-4 text-sm font-semibold font-montserrat border-b-2 transition-colors flex items-center gap-1.5 ${
                    activeTabIndex === i
                      ? activeColor
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {isExpress && <Zap className="w-3 h-3" />}
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTabIndex === i ? activeBadge : 'bg-slate-100 text-slate-500'
                  }`}>
                    {tab.items.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={
                isExpressTab
                  ? 'Buscar por ID, nombre, teléfono o dirección...'
                  : showPostponedReason
                    ? 'Buscar por ID, nombre, teléfono, distrito o motivo...'
                    : 'Buscar por ID, nombre, teléfono o distrito...'
              }
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/30 focus:border-[#B1336E] font-montserrat"
            />
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Search className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm font-montserrat">Sin resultados</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((d, idx) => (
                <li
                  key={d.id ?? idx}
                  className="px-5 py-3 hover:bg-slate-50 transition-colors"
                >
                  {isExpressTab ? (
                    /* ── Express row ── */
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-3">
                        {/* ID badge — green for express */}
                        <span className="shrink-0 text-xs font-bold text-brand-green bg-brand-green/10 px-2 py-1 rounded font-montserrat min-w-[2.5rem] text-center">
                          #{d.id}
                        </span>

                        {/* Name + phone */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 font-montserrat truncate">
                            {d.recipient_name ?? '—'}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat">
                            {d.recipient_phone ?? '—'}
                          </p>
                        </div>

                        {/* Vehicle + price */}
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full text-white font-montserrat ${d.vehicle_type === 'bike' ? 'bg-brand-green' : 'bg-brand-primary'}`}>
                            {d.vehicle_type === 'bike' ? '🏍 Moto' : '🚗 Auto'}
                          </span>
                          <span className="text-xs font-extrabold text-brand-green font-montserrat">
                            S/ {Number(d.total_price).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Status + distance */}
                      <div className="ml-[calc(2.5rem+0.75rem)] flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full font-montserrat ${getStatusColor(d.status)}`}>
                          {getStatusLabel(d.status)}
                        </span>
                        <span className="text-xs text-slate-400 font-montserrat flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {d.distance_km} km
                        </span>
                      </div>

                      {/* Pickup → Dropoff */}
                      <div className="ml-[calc(2.5rem+0.75rem)] space-y-0.5">
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3 h-3 text-brand-primary shrink-0 mt-0.5" />
                          <p className="text-xs text-slate-500 font-montserrat leading-snug truncate">
                            {d.pickup_address ?? '—'}
                          </p>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3 h-3 text-brand-green shrink-0 mt-0.5" />
                          <p className="text-xs text-slate-500 font-montserrat leading-snug truncate">
                            {d.dropoff_address ?? '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* ── Standard row (Lima / Provincia / Postergados) ── */
                    <div className="flex items-center gap-3">
                      <span className="shrink-0 text-xs font-bold text-[#B1336E] bg-[#B1336E]/10 px-2 py-1 rounded font-montserrat min-w-[2.5rem] text-center">
                        #{d.id}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 font-montserrat truncate">
                          {d.recipient_name ?? d.name ?? '—'}
                        </p>
                        <p className="text-xs text-slate-500 font-montserrat">
                          {d.recipient_phone ?? d.phone ?? '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 text-xs text-slate-500 font-montserrat">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[7rem]">
                          {d.district ?? d.recipient_district ?? '—'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Postponed reason */}
                  {showPostponedReason && d.postponed_reason && (
                    <div className="mt-1.5 ml-[calc(2.5rem+0.75rem)] flex items-start gap-1.5">
                      <AlarmClock className="w-3 h-3 text-sky-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-sky-700 font-montserrat leading-snug">
                        {d.postponed_reason}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}